/**
 * Composant QuestionnairesTab pour la page Satisfaction
 * Affiche la liste des questionnaires avec dates, statuts, filtre et relance
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { FileText, Mail, AlertTriangle, CheckCircle, RefreshCw, Trash2, Star, Clock, Send } from 'lucide-react';

const getStatutBadge = (statut) => {
  const config = {
    'envoye': { label: 'Envoyé', color: 'bg-blue-100 text-blue-700', icon: Mail },
    'relance1': { label: 'Relance 1', color: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
    'relance2': { label: 'Relance 2', color: 'bg-orange-100 text-orange-700', icon: AlertTriangle },
    'complete': { label: 'Reçu', color: 'bg-green-100 text-green-700', icon: CheckCircle }
  };
  const c = config[statut] || config.envoye;
  const Icon = c.icon;
  return (
    <Badge className={c.color}>
      <Icon className="h-3 w-3 mr-1" />
      {c.label}
    </Badge>
  );
};

const formatDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const isOverdue = (q) => {
  if (q.statut === 'complete') return false;
  const created = new Date(q.created_at);
  const now = new Date();
  const diffDays = (now - created) / (1000 * 60 * 60 * 24);
  return diffDays >= 7;
};

export const QuestionnairesTab = ({ 
  questionnaires, 
  types, 
  onSendClick, 
  onViewReponses, 
  onRelance, 
  onDelete,
  onAutoRelance 
}) => {
  const [filter, setFilter] = useState('tous');

  if (questionnaires.length === 0) {
    return (
      <Card data-testid="questionnaires-tab-empty">
        <CardContent className="text-center py-12">
          <FileText className="h-12 w-12 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500 mb-4">Aucun questionnaire envoyé</p>
          <Button onClick={onSendClick} className="bg-[#8DC63F] hover:bg-[#7AB62F]">
            Envoyer le premier questionnaire
          </Button>
        </CardContent>
      </Card>
    );
  }

  const aRelancer = questionnaires.filter(q => q.statut !== 'complete' && isOverdue(q));
  const recus = questionnaires.filter(q => q.statut === 'complete');
  const enAttente = questionnaires.filter(q => q.statut !== 'complete' && !isOverdue(q));

  const filtered = filter === 'tous' ? questionnaires
    : filter === 'relancer' ? aRelancer
    : filter === 'recus' ? recus
    : enAttente;

  return (
    <div className="space-y-3" data-testid="questionnaires-tab">
      {/* KPIs rapides */}
      <div className="grid grid-cols-4 gap-3">
        <button
          onClick={() => setFilter('tous')}
          data-testid="filter-tous"
          className={`p-3 rounded-lg border text-center transition-colors ${filter === 'tous' ? 'bg-slate-100 border-slate-400' : 'bg-white border-slate-200 hover:bg-slate-50'}`}
        >
          <p className="text-xl font-bold text-slate-700">{questionnaires.length}</p>
          <p className="text-xs text-slate-500">Total</p>
        </button>
        <button
          onClick={() => setFilter('relancer')}
          data-testid="filter-relancer"
          className={`p-3 rounded-lg border text-center transition-colors ${filter === 'relancer' ? 'bg-orange-100 border-orange-400' : 'bg-white border-slate-200 hover:bg-orange-50'}`}
        >
          <p className="text-xl font-bold text-orange-600">{aRelancer.length}</p>
          <p className="text-xs text-orange-600">A relancer</p>
        </button>
        <button
          onClick={() => setFilter('recus')}
          data-testid="filter-recus"
          className={`p-3 rounded-lg border text-center transition-colors ${filter === 'recus' ? 'bg-green-100 border-green-400' : 'bg-white border-slate-200 hover:bg-green-50'}`}
        >
          <p className="text-xl font-bold text-green-600">{recus.length}</p>
          <p className="text-xs text-green-600">Reçus</p>
        </button>
        <button
          onClick={() => setFilter('attente')}
          data-testid="filter-attente"
          className={`p-3 rounded-lg border text-center transition-colors ${filter === 'attente' ? 'bg-blue-100 border-blue-400' : 'bg-white border-slate-200 hover:bg-blue-50'}`}
        >
          <p className="text-xl font-bold text-blue-600">{enAttente.length}</p>
          <p className="text-xs text-blue-600">En attente</p>
        </button>
      </div>

      {/* Bouton relance auto */}
      {aRelancer.length > 0 && onAutoRelance && (
        <div className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-orange-500" />
            <span className="text-sm text-orange-700">
              <strong>{aRelancer.length}</strong> questionnaire(s) sans réponse depuis plus de 7 jours
            </span>
          </div>
          <Button 
            onClick={onAutoRelance} 
            size="sm" 
            variant="outline"
            className="border-orange-300 text-orange-700 hover:bg-orange-100"
            data-testid="auto-relance-btn"
          >
            <Send className="h-4 w-4 mr-1" />
            Relancer tous par email
          </Button>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Réponses aux questionnaires
            {filter !== 'tous' && (
              <Badge variant="outline" className="ml-2">
                {filter === 'relancer' ? 'A relancer' : filter === 'recus' ? 'Reçus' : 'En attente'}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-3">Nom</th>
                  <th className="text-left p-3">Type</th>
                  <th className="text-left p-3">Statut</th>
                  <th className="text-left p-3">Date envoi</th>
                  <th className="text-left p-3">Dernière relance</th>
                  <th className="text-left p-3">Date réponse</th>
                  <th className="text-center p-3">Note</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map(q => {
                  let moyenneQ = null;
                  if (q.reponses) {
                    const notes = Object.values(q.reponses).filter(v => typeof v === 'number' && v >= 1 && v <= 5);
                    if (notes.length > 0) {
                      moyenneQ = (notes.reduce((a, b) => a + b, 0) / notes.length).toFixed(1);
                    }
                  }
                  const overdue = isOverdue(q);
                  
                  return (
                    <tr key={q.questionnaire_id} className={`hover:bg-slate-50 ${overdue ? 'bg-orange-50/50' : ''}`}>
                      <td className="p-3">
                        <p className="font-medium">{q.destinataire_nom}</p>
                        <p className="text-xs text-slate-500">{q.destinataire_email}</p>
                      </td>
                      <td className="p-3">
                        <Badge variant="outline">{types[q.type]?.label || q.type}</Badge>
                      </td>
                      <td className="p-3">
                        {getStatutBadge(q.statut)}
                        {overdue && q.statut !== 'complete' && (
                          <span className="block text-xs text-orange-500 mt-1">+7 jours</span>
                        )}
                      </td>
                      <td className="p-3 text-slate-600 text-xs">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3 text-slate-400" />
                          {formatDate(q.created_at)}
                        </div>
                      </td>
                      <td className="p-3 text-slate-600 text-xs">
                        {q.last_relance ? (
                          <div>
                            <div className="flex items-center gap-1">
                              <RefreshCw className="h-3 w-3 text-amber-500" />
                              {formatDate(q.last_relance)}
                            </div>
                            <span className="text-xs text-amber-600">{q.relances || 0} relance(s)</span>
                          </div>
                        ) : '-'}
                      </td>
                      <td className="p-3 text-slate-600 text-xs">
                        {q.responded_at ? (
                          <div className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="h-3 w-3" />
                            {formatDate(q.responded_at)}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="p-3 text-center">
                        {moyenneQ ? (
                          <div className="flex items-center justify-center gap-1">
                            <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
                            <span className="font-medium">{moyenneQ}/5</span>
                          </div>
                        ) : '-'}
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {q.statut === 'complete' ? (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => onViewReponses(q)}
                              data-testid={`view-responses-${q.questionnaire_id}`}
                            >
                              Voir détails
                            </Button>
                          ) : (q.relances || 0) < 2 && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => onRelance(q.questionnaire_id)}
                              data-testid={`relance-${q.questionnaire_id}`}
                              className={overdue ? 'border-orange-300 text-orange-700 hover:bg-orange-50' : ''}
                            >
                              <Mail className="h-4 w-4 mr-1" />
                              Relancer
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => onDelete(q.questionnaire_id)}
                            data-testid={`delete-${q.questionnaire_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default QuestionnairesTab;
